import { Dates } from "./constants/enums";

export const calculateSumPerDay = (isLT, sum, rate) =>
    isLT
        ? Math.round(
              sum * (Math.round((rate / 30) * 1000) / 100 / 1000) * 100
          ) / 100
        : (sum * rate) / 100 / 30;

export const calculateCommission = sum =>
    sum > 300000 ? 10000 : sum > 150000 ? 7000 : 5000;

export const calculateMonthlyPayment = (sum, rate) =>
    Math.floor((sum * rate) / 100);

export const calculatePrepaymentLT = (sum, actualRate, used, duration) =>
    ((((used * actualRate) / 100) * sum) / duration) ^ 0;

export const calculatePayedDays = (pDate, today) =>
    Math.floor((pDate - today) / Dates._1Day);

export const calculateUsedDays = (isLT, pDate, today) =>
    Math.floor(
        (isLT ? today - pDate : Dates._30Days - (pDate - today)) / Dates._1Day
    );

export const calculateDelta = (isLT, today, exemptionDate, pDate) =>
    ((isLT ? today - exemptionDate : today - pDate) / Dates._1Day) ^ 0;

export const calculatePenalty = (isLT, sum, delta) =>
    isLT ? 0 : Math.max(((sum * 0.015 * delta) / 5) ^ 0, 0);

export const calculatePenaltyPerDay = (isLT, sum) =>
    isLT ? 0 : ((sum * 0.015) / 5) ^ 0;

export const calculateFullPenalty = (isLT, sum, delta) =>
    isLT ? 0 : Math.max((sum * 0.015 * delta) ^ 0, 0);

export const calculatePercents = (isLT, sum, rate, duration, used) =>
    (isLT ? (((used * rate) / 100) * sum) / duration : (sum * rate) / 100) ^ 0;

export const calculateBalance = (payedDays, sumPerDay) =>
    Math.floor(Math.max(0, payedDays * sumPerDay));

export const calculateRecommendedSum = (sumPerDay, usedDays) =>
    Math.floor(Math.max(0, Math.min(30 * sumPerDay, usedDays * sumPerDay)));

export const calculateRecommendedSumLT = (sumPerDay, usedDays) =>
    Math.floor(Math.max(0, usedDays * sumPerDay));

export const calculatePaymentDebt = (sumPerDay, usedDays) =>
    Math.floor(Math.max(0, usedDays * sumPerDay));

export const calculateActualRate = (rates, numProlongations) =>
    rates[Math.min(rates.length - 1, numProlongations)];

export const calculateLeasingTotalSum = (sum, duration, rate) =>
    sum + duration * Math.floor((sum * rate) / 100);

export const calculateLeasingPaymentPerMonth = (s, duration) =>
    Math.floor(s / duration);

export const calculateLeasingPaymentBuyout = (
    sum,
    duration,
    numPayments,
    buyoutPerMonth
) => (numPayments === duration - 1 ? 0 : sum - buyoutPerMonth * numPayments);

export const calculatePreferentialPeriodTotalPayment = (
    sum,
    duration,
    buyoutPerMonth
) => Math.floor(sum / duration - buyoutPerMonth + 100);

export const generatePayments = program => {
    const { creationDate, duration, rates, sum } = program;
    const date = new Date(creationDate);

    return Array(Number(duration))
        .fill(0)
        .map((_, index) => {
            const actualRate = rates[Math.min(rates.length - 1, index)];
            return {
                paymentDate: new Date(+date + Dates._30Days * (index + 1)),
                paymentSum: (sum * actualRate) / 100
            };
        });
};

export const generateLeasingPayments = leasingProgram => {
    const {
        date,
        duration,
        leasingRate,
        sum,
        sumLog = [],
        isLeasingPrepayed,
        isPreferentialRepayment,
        preferentialPeriod
    } = leasingProgram;

    const notPreferentialPeriod = duration - preferentialPeriod;

    // in the period before the specified date (26.03.2019), the preferential payment was 100 rubles, after it was canceled

    const preferentialPeriodCarPayment =
        Number(date) >= Number(new Date(2019, 2, 25)) ? 0 : 100;

    return Array(+duration)
        .fill(0)
        .map((_, index) => {
            const isPreferentialPeriod = index < preferentialPeriod;
            const isLastMonth = index === duration - 1;

            const paymentDate = new Date(
                +date + Dates._30Days * (index + (isLeasingPrepayed ? 0 : 1))
            );
            const earlierSums = sumLog.filter(
                ({ date, sum }) => +new Date(date) > +paymentDate
            );
            const thisMonthSum =
                sumLog.length === 0
                    ? sum
                    : earlierSums.length === 0
                    ? sum
                    : Number(earlierSums[earlierSums.length - 1].sum);

            const totalSum = calculateLeasingTotalSum(
                thisMonthSum,
                duration,
                leasingRate
            );
            const totalPerMonth = calculateLeasingPaymentPerMonth(
                totalSum,
                duration
            );
            const buyoutPerMonth = calculateLeasingPaymentPerMonth(
                thisMonthSum,
                duration
            );

            const preferentialPeriodTotalPayment = Math.floor(
                totalSum / duration -
                    buyoutPerMonth +
                    preferentialPeriodCarPayment
            );
            const notPreferentialPeriodTotalPayment = Math.floor(
                (totalSum -
                    preferentialPeriod * preferentialPeriodTotalPayment) /
                    notPreferentialPeriod
            );

            const lastPreferentialPeriodTotalPayment =
                totalSum -
                preferentialPeriodTotalPayment * preferentialPeriod -
                notPreferentialPeriodTotalPayment * (notPreferentialPeriod - 1);
            const notPreferentialPeriodCarPayment = Math.floor(
                (thisMonthSum -
                    preferentialPeriodCarPayment * preferentialPeriod) /
                    notPreferentialPeriod
            );
            const lastPreferentialPeriodCarPayment =
                thisMonthSum -
                preferentialPeriodCarPayment * preferentialPeriod -
                notPreferentialPeriodCarPayment * (notPreferentialPeriod - 1);

            return {
                paymentDate: paymentDate,
                paymentTotal: isPreferentialRepayment
                    ? isPreferentialPeriod
                        ? preferentialPeriodTotalPayment
                        : isLastMonth
                        ? lastPreferentialPeriodTotalPayment
                        : notPreferentialPeriodTotalPayment
                    : isLastMonth
                    ? totalSum - totalPerMonth * (duration - 1)
                    : totalPerMonth,
                paymentCar: isPreferentialRepayment
                    ? isPreferentialPeriod
                        ? preferentialPeriodCarPayment
                        : isLastMonth
                        ? lastPreferentialPeriodCarPayment
                        : notPreferentialPeriodCarPayment
                    : isLastMonth
                    ? thisMonthSum - buyoutPerMonth * (duration - 1)
                    : buyoutPerMonth,
                paymentBuyout: isPreferentialRepayment
                    ? isPreferentialPeriod
                        ? thisMonthSum -
                          preferentialPeriodCarPayment * (index + 1)
                        : isLastMonth
                        ? ""
                        : thisMonthSum -
                          preferentialPeriodCarPayment * preferentialPeriod -
                          notPreferentialPeriodCarPayment *
                              (index - preferentialPeriod + 1)
                    : isLastMonth
                    ? ""
                    : thisMonthSum - buyoutPerMonth * (index + 1)
            };
        });
};

export const generateCreditPayment = ({
    sumLog,
    sum,
    pDate,
    rate,
    lastPaymentDay
}) => {
    const p = sumLog.reduce((acc, { date, sum }) => {
        const d = new Date(date);
        const startDate =
            acc.length > 0
                ? new Date(acc[acc.length - 1].endDate + Dates._1Day)
                : pDate;

        const isDateInsideRange = d > pDate && d < lastPaymentDay;
        const numDays = Math.ceil((d - startDate) / Dates._1Day);
        const sumPerDay =
            Math.round(((Math.abs(sum) * rate) / 100 / 365) * 100) / 100;

        return acc.concat(
            isDateInsideRange
                ? {
                      startDate,
                      endDate: new Date(date),
                      numDays,
                      sum,
                      sumPerDay,
                      totalSum: sumPerDay * numDays
                  }
                : []
        );
    }, []);

    const startDate =
        p.length > 0 ? new Date(p[p.length - 1].endDate + Dates._1Day) : pDate;
    const numDays = Math.floor((lastPaymentDay - startDate) / Dates._1Day);
    const filteredSumLog = sumLog.filter(
        ({ date }) => lastPaymentDay < new Date(date)
    );
    const actualSum = filteredSumLog.length > 0 ? filteredSumLog[0].sum : sum;
    const sumPerDay = Math.round((Math.abs(actualSum) * rate) / 100 / 365);

    return p.concat({
        startDate,
        endDate: lastPaymentDay,
        numDays,
        sum,
        sumPerDay: Math.round((Math.abs(sum) * rate) / 100 / 365),
        totalSum: sumPerDay * numDays
    });
};



// WEBPACK FOOTER //
// ./src/businessLogic.js